import { useEffect, useMemo, useState } from 'react'
import Layout from 'containers/Layout'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { preBookingsApiGetFunnel, preBookingsApiGetOverview } from 'lib/network/apiGet'
import { abandonmentRate, conversionRate, toFunnelStages } from './preBookingHelpers'

const StatCard = ({ title, value, description }) => (
  <div className="bg-white overflow-hidden shadow rounded-lg h-full">
    <div className="p-4 sm:p-5">
      <dl>
        <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
        <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
        {description ? <p className="mt-1.5 text-xs leading-4 text-gray-400">{description}</p> : null}
      </dl>
    </div>
  </div>
)

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-medium">{label}</p>
        {payload.map(entry => (
          <p key={entry.dataKey} style={{ color: entry.color }}>
            {entry.value}
          </p>
        ))}
      </div>
    )
  }
  return null
}

const PreBookingsFunnel = () => {
  const { t } = useTranslation()
  const [funnel, setFunnel] = useState(null)
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [range, setRange] = useState({ from: '', to: '' })

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true)
      try {
        const params = {}
        if (range.from) params.from = range.from
        if (range.to) params.to = range.to
        const [funnelRes, overviewRes] = await Promise.all([preBookingsApiGetFunnel(params), preBookingsApiGetOverview(params)])
        setFunnel(funnelRes?.data || null)
        setOverview(overviewRes?.data || null)
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [range.from, range.to])

  const stages = useMemo(
    () =>
      toFunnelStages(funnel, {
        carViews: t('preBookings.funnel.carViews'),
        datesSelected: t('preBookings.funnel.datesSelected'),
        readyForConfirmation: t('preBookings.funnel.ready'),
        confirmed: t('preBookings.funnel.confirmed'),
        abandoned: t('preBookings.funnel.abandoned'),
      }),
    [funnel, t]
  )

  return (
    <Layout
      title={t('preBookings.funnel.title')}
      breadcrumbs={[{ name: t('preBookings.breadcrumb'), pathname: '/pre-bookings' }]}
      headerRight={
        <div className="flex items-center gap-2">
          <input
            type="date"
            className="text-sm bg-gray-100 border-0 rounded-sm px-2 py-1.5"
            value={range.from}
            onChange={e => setRange(prev => ({ ...prev, from: e.target.value }))}
          />
          <input type="date" className="text-sm bg-gray-100 border-0 rounded-sm px-2 py-1.5" value={range.to} onChange={e => setRange(prev => ({ ...prev, to: e.target.value }))} />
        </div>
      }
    >
      {loading ? (
        <div className="h-[220px] flex items-center justify-center">
          <LoadingIcon />
        </div>
      ) : (
        <div className="p-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Car views are intents created — an intent is created on every car
                view, so there is no separate view counter to reconcile against. */}
            <StatCard title={t('preBookings.funnel.carViews')} value={(funnel?.carViews || 0).toLocaleString()} description={t('preBookings.funnel.carViewsHint')} />
            <StatCard title={t('preBookings.funnel.confirmed')} value={(funnel?.confirmed || 0).toLocaleString()} />
            <StatCard title={t('preBookings.funnel.conversionRate')} value={`${conversionRate(funnel)}%`} />
            <StatCard title={t('preBookings.funnel.abandonmentRate')} value={`${abandonmentRate(funnel)}%`} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard title={t('preBookings.funnel.open')} value={(overview?.open || 0).toLocaleString()} />
            <StatCard title={t('preBookings.funnel.guests')} value={(overview?.guests || 0).toLocaleString()} description={t('preBookings.funnel.guestsHint')} />
            <StatCard title={t('preBookings.funnel.last24h')} value={(overview?.last24h || 0).toLocaleString()} />
            <StatCard title={t('preBookings.funnel.last7d')} value={(overview?.last7d || 0).toLocaleString()} />
          </div>

          {/* Recharts' FunnelChart is not used anywhere in this codebase, so this
              stays a horizontal BarChart like every other chart here. */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('preBookings.funnel.stagesTitle')}</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={stages} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#666" allowDecimals={false} />
                <YAxis type="category" dataKey="label" stroke="#666" width={140} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#8884d8" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {funnel?.daily?.length ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('preBookings.funnel.dailyTitle')}</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={[...funnel.daily].reverse()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" stroke="#666" />
                  <YAxis stroke="#666" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="carViews" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="confirmed" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="abandoned" fill="#ff6b6b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </div>
      )}
    </Layout>
  )
}

export default PreBookingsFunnel
